import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Zap, Plus, Pencil, Trash2, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const fill = (body, landlord, property) =>
  (body || "")
    .replace(/\{landlord\}/gi, landlord || "there")
    .replace(/\{property\}/gi, property || "your property");

export const QuickReplies = ({ onPick, landlordName = "", propertyRef = "", align = "left", compact = false }) => {
  const [open, setOpen] = useState(false);
  const [manage, setManage] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const ref = useRef(null);

  const load = () =>
    api.get("/message-templates")
      .then(({ data }) => { setTemplates(data.templates || []); setLoaded(true); })
      .catch(() => setLoaded(true));

  useEffect(() => { if ((open || manage) && !loaded) load(); /* eslint-disable-next-line */ }, [open, manage]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const pick = (t) => { onPick(fill(t.body, landlordName, propertyRef)); setOpen(false); };

  return (
    <div className="relative" ref={ref}>
      {compact ? (
        <button type="button" data-testid="quick-replies-btn" onClick={() => setOpen((o) => !o)}
          className="w-10 h-10 shrink-0 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 flex items-center justify-center" title="Insert a saved reply">
          <Zap size={16} className="text-amber-500" />
        </button>
      ) : (
        <button type="button" data-testid="quick-replies-btn" onClick={() => setOpen((o) => !o)}
          className="flex items-center gap-1.5 text-xs font-medium text-slate-600 border border-slate-300 rounded-lg px-2.5 py-1.5 hover:bg-slate-50" title="Insert a saved reply">
          <Zap size={14} className="text-amber-500" /> Templates
        </button>
      )}
      {open && (
        <div data-testid="quick-replies-popover"
          className={`absolute z-[55] ${compact ? "bottom-full mb-2" : "top-full mt-2"} ${align === "right" ? "right-0" : "left-0"} w-72 bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden`}>
          <div className="max-h-64 overflow-y-auto">
            {!loaded ? <p className="text-xs text-slate-400 p-3">Loading…</p>
              : templates.length === 0 ? <p className="text-xs text-slate-400 p-3">No templates yet — add one below.</p>
                : templates.map((t) => (
                  <button key={t.template_id} type="button" onClick={() => pick(t)} data-testid={`quick-reply-${t.template_id}`}
                    className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-slate-50 last:border-0">
                    <p className="text-sm font-medium text-slate-800">{t.title}</p>
                    <p className="text-xs text-slate-400 truncate">{t.body}</p>
                  </button>
                ))}
          </div>
          <button type="button" data-testid="quick-replies-manage" onClick={() => { setOpen(false); setManage(true); }}
            className="w-full text-left px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-50 flex items-center gap-1.5 border-t border-slate-100">
            <Pencil size={12} /> Manage templates
          </button>
        </div>
      )}
      {manage && <ManageModal templates={templates} reload={load} onClose={() => setManage(false)} />}
    </div>
  );
};

const ManageModal = ({ templates, reload, onClose }) => {
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const startNew = () => { setEditing({ new: true }); setTitle(""); setBody(""); };
  const startEdit = (t) => { setEditing(t); setTitle(t.title); setBody(t.body); };

  const save = async () => {
    if (!title.trim()) { toast.error("Add a title"); return; }
    setBusy(true);
    try {
      if (editing?.new) await api.post("/message-templates", { title: title.trim(), body });
      else await api.patch(`/message-templates/${editing.template_id}`, { title: title.trim(), body });
      await reload(); setEditing(null); toast.success("Template saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template “${t.title}”?`)) return;
    try { await api.delete(`/message-templates/${t.template_id}`); await reload(); toast.success("Template deleted"); }
    catch { toast.error("Could not delete"); }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm" onClick={onClose}>
      <div data-testid="manage-templates-modal" onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-xl border border-slate-200 w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between shrink-0">
          <p className="font-display text-base font-semibold text-slate-900 flex items-center gap-2"><Zap size={16} className="text-amber-500" /> Quick reply templates</p>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={20} /></button>
        </div>
        <div className="p-4 overflow-y-auto flex-1">
          {editing ? (
            <div className="space-y-3" data-testid="template-editor">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
                <input data-testid="template-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Rent received"
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Message</label>
                <textarea data-testid="template-body" value={body} onChange={(e) => setBody(e.target.value)} rows={6}
                  placeholder="Write the reply… use {landlord} and {property} to auto-fill."
                  className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600" />
                <p className="text-xs text-slate-400 mt-1">Tip: <b>{"{landlord}"}</b> and <b>{"{property}"}</b> are replaced automatically when you insert this reply.</p>
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={() => setEditing(null)} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
                <button onClick={save} disabled={busy} data-testid="template-save"
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
                  {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Save
                </button>
              </div>
            </div>
          ) : (
            <>
              <button onClick={startNew} data-testid="template-add"
                className="w-full mb-3 flex items-center justify-center gap-1.5 text-sm font-semibold text-emerald-700 border border-dashed border-emerald-300 rounded-lg px-3 py-2 hover:bg-emerald-50">
                <Plus size={15} /> New template
              </button>
              <div className="space-y-2">
                {templates.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No templates yet.</p>}
                {templates.map((t) => (
                  <div key={t.template_id} className="rounded-lg border border-slate-200 p-3" data-testid={`template-row-${t.template_id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-slate-800">{t.title}</p>
                        <p className="text-xs text-slate-400 whitespace-pre-wrap line-clamp-2">{t.body}</p>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <button onClick={() => startEdit(t)} data-testid={`template-edit-${t.template_id}`} className="p-1.5 text-slate-400 hover:text-emerald-600" title="Edit"><Pencil size={14} /></button>
                        <button onClick={() => remove(t)} data-testid={`template-delete-${t.template_id}`} className="p-1.5 text-slate-400 hover:text-red-500" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default QuickReplies;
